import React, { useState } from 'react';
import { ArrowLeft, Camera, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { photographerData } from '../../mockDemos';
import { useNavigate } from 'react-router-dom';

export const PhotographerDemo = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', 'Wedding', 'Pre-Wedding', 'Portrait', 'Family', 'Event', 'Product'];
  const filteredPortfolio = selectedCategory === 'All' 
    ? photographerData.portfolio 
    : photographerData.portfolio.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Camera className="text-orange-500" size={32} />
              <span className="text-2xl font-bold text-gray-900">{photographerData.name}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-orange-500 text-orange-500 hover:bg-orange-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Portfolio
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <Badge className="bg-orange-500 text-white hover:bg-orange-600">
            Demo Website by Pranshik
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold">
            {photographerData.hero.headline}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {photographerData.hero.subtext}
          </p>
          <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
            Book a Session
          </Button>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Portfolio</h2>
            <p className="text-xl text-gray-600">Moments captured, memories preserved</p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-500 text-orange-500 hover:bg-orange-50'}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPortfolio.map((item) => (
              <div key={item.id} className="relative overflow-hidden rounded-lg aspect-[4/3] group cursor-pointer">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <Badge className="mb-2 bg-orange-500 text-white">{item.category}</Badge>
                    <h3 className="text-xl font-bold text-white">{item.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Packages</h2>
            <p className="text-xl text-gray-600">Flexible options for every budget</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {photographerData.packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`border-2 transition-all duration-300 ${
                  pkg.popular ? 'border-orange-500 shadow-xl scale-105' : 'hover:border-orange-500'
                }`}
              >
                <CardHeader className="text-center">
                  {pkg.popular && (
                    <Badge className="mx-auto mb-2 bg-orange-500 text-white">Most Popular</Badge>
                  )}
                  <CardTitle className="text-3xl">{pkg.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-orange-500">{pkg.price}</span>
                  </div>
                  <p className="text-gray-600 mt-2">{pkg.duration}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle2 className="text-orange-500" size={18} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-6 ${
                      pkg.popular
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'border-orange-500 text-orange-500 hover:bg-orange-50'
                    }`}
                    variant={pkg.popular ? 'default' : 'outline'}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <Camera size={28} />
            <span className="text-2xl font-bold">{photographerData.name}</span>
          </div>
          <p className="text-gray-400">{photographerData.tagline}</p>
          <div className="pt-4 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              This is a demo website created by <span className="text-orange-500 font-semibold">Pranshik</span>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Want a website like this? <a href="/" className="text-orange-500 hover:underline">Contact me</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
